import { CompanyService } from '@beeboard/common';
import { defineStore } from 'pinia';
import { useCompanyTagRulesStore } from './companyTagRulesStore';
import type {
  CompanyTag,
  CreateCompanyTagRequest,
  UpdateCompanyTagRequest,
} from '@beeboard/common';

export type CompanyTagStoreState = {
  companyTags: CompanyTag[];
};

export const useCompanyTagStore = defineStore('companyTagStore', {
  state: (): CompanyTagStoreState => ({
    companyTags: [] as CompanyTag[],
  }),

  getters: {
    companyTag(state) {
      return (companyTagId: string): CompanyTag => {
        const tag = state.companyTags.find((tag) => tag.id === companyTagId);
        if (!tag) {
          throw new Error(
            `Missing companyTag ${companyTagId} in companyTag store`,
          );
        }

        return tag;
      };
    },

    hasCompanyTag(state) {
      return (companyTagId: string): boolean => {
        return state.companyTags.some((tag) => tag.id === companyTagId);
      };
    },

    tags(state) {
      return state.companyTags.filter((tag) => !tag.topic);
    },

    topics(state) {
      console.log(state.companyTags);

      return state.companyTags.filter((tag) => tag.topic);
    },
  },

  actions: {
    write(tag: CompanyTag) {
      const tags = this.$state.companyTags;
      const oldTag = tags.find((t) => t.id === tag.id);
      if (oldTag) {
        Object.assign(oldTag, tag);
      } else {
        tags.push(tag);
      }
    },

    async loadCompanyTags() {
      const companyTags = await CompanyService.getCompanyTags();
      this.$patch({ companyTags });
    },

    async loadCompanyTag(tagId: string) {
      const tag = await CompanyService.getCompanyTag(tagId);
      this.write(tag);
    },

    async create(payload: CreateCompanyTagRequest) {
      const errors =
        useCompanyTagRulesStore().validateCreateCompanyTagRequest(payload);
      if (errors) {
        throw errors;
      }
      const tag = await CompanyService.createCompanyTag(payload);
      this.write(tag);
    },

    async update(tagId: string, payload: UpdateCompanyTagRequest) {
      const errors = useCompanyTagRulesStore().validateUpdateCompanyTagRequest(
        payload,
        tagId,
      );
      if (errors) {
        throw errors;
      }
      const tag = await CompanyService.updateCompanyTag(tagId, payload);
      this.write(tag);
    },

    async delete(tagId: string) {
      const errors =
        useCompanyTagRulesStore().validateRemoveCompanyTagRequest(tagId);
      if (errors) {
        throw errors;
      }
      await CompanyService.deleteCompanyTag(tagId);
      const tags = this.$state.companyTags;
      const tagIndex = tags.findIndex((t) => t.id === tagId);
      if (tagIndex > -1) {
        tags.splice(tagIndex, 1);
      }
    },
  },
});
